import { lazy, Suspense, useEffect, useRef, useState } from "react";

/**
 * LazyOnVisible — render a lazy component only when its placeholder enters the
 * viewport (or within `rootMargin`). Cuts JS off the critical path for
 * below-the-fold sections without hurting SEO because the placeholder still
 * lives in the SSR-friendly DOM and the fallback occupies real space so CLS
 * stays at 0.
 *
 * Usage:
 *   const Testimonials = lazy(() => import("./Testimonials"));
 *   <LazyOnVisible loader={() => <Skeleton />}>
 *     <Testimonials />
 *   </LazyOnVisible>
 *
 * Or use the shorthand `lazyOnVisible(loader, opts)` factory.
 */
export function LazyOnVisible({ children, fallback = null, rootMargin = "200px" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    if (!("IntersectionObserver" in window)) {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin },
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [visible, rootMargin]);

  return (
    <div ref={ref} data-lazy-visible={visible ? "loaded" : "pending"}>
      {visible ? <Suspense fallback={fallback}>{children}</Suspense> : fallback}
    </div>
  );
}

/**
 * lazyOnVisible — helper that combines React.lazy + LazyOnVisible into one call.
 * Returns a component that only imports + renders when scrolled into range.
 */
export function lazyOnVisible(loader, { fallback = null, rootMargin = "200px" } = {}) {
  const Lazy = lazy(loader);
  return function LazyWhenVisible(props) {
    return (
      <LazyOnVisible fallback={fallback} rootMargin={rootMargin}>
        <Lazy {...props} />
      </LazyOnVisible>
    );
  };
}

/** Simple space-preserving skeleton so CLS stays at 0 while we wait. */
export function SectionSkeleton({ h = 400 }) {
  return (
    <div
      aria-hidden="true"
      style={{ minHeight: h }}
      className="w-full bg-transparent"
    />
  );
}
